import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { api, type CurrentUser, UnauthenticatedError } from "./api.ts";
import { clearStoredSessionId, getStoredSessionId } from "./auth.ts";
import { Footer } from "./components/footer.tsx";
import { Layout } from "./components/layout.tsx";
import { SignIn } from "./pages/sign-in.tsx";
import { Dashboard } from "./pages/dashboard.tsx";
import { Application } from "./pages/application/index.tsx";
import { OwnerCheckEmail } from "./pages/auth/check-email.tsx";
import { OwnerLinkInvalid } from "./pages/auth/link-invalid.tsx";
import { OidcSignIn } from "./pages/oidc/sign-in.tsx";
import { TestCallback } from "./pages/test-callback.tsx";
import { Terms } from "./pages/terms.tsx";
import { Privacy } from "./pages/privacy.tsx";

export function App() {
  return (
    <BrowserRouter>
      <div className="flex min-h-screen flex-col">
        <div className="flex-1">
          <Routes>
            {/* Public — rendered without an owner session */}
            <Route path="/oidc/:appId/authorize" element={<OidcSignIn />} />
            <Route path="/auth/check-email" element={<OwnerCheckEmail />} />
            <Route path="/auth/link-invalid" element={<OwnerLinkInvalid />} />
            <Route path="/test-callback" element={<TestCallback />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            {/* Everything else is owner-gated */}
            <Route path="/*" element={<OwnerArea />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

type AuthStatus = "loading" | "signed-in" | "signed-out";

function OwnerArea() {
  const [authStatus, setAuthStatus] = useState<AuthStatus>("loading");
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    if (!getStoredSessionId()) {
      setAuthStatus("signed-out");
      return;
    }

    api
      .getCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        setAuthStatus("signed-in");
      })
      .catch((error) => {
        if (error instanceof UnauthenticatedError) {
          setAuthStatus("signed-out");
          return;
        }
        console.error(error);
        setAuthStatus("signed-out");
      });
  }, []);

  async function handleSignOut() {
    try {
      await api.logout();
    } catch (error) {
      console.error(error);
    } finally {
      clearStoredSessionId();
      setCurrentUser(null);
      setAuthStatus("signed-out");
    }
  }

  if (authStatus === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted">
        Loading…
      </div>
    );
  }

  if (authStatus === "signed-out") {
    return <SignIn />;
  }

  return (
    <Layout userEmail={currentUser?.email} onSignOut={handleSignOut}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="applications/:appId" element={<Application />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
